import { FC, lazy, Suspense, useCallback, useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useParams } from 'react-router'

import { CommentOutlined } from '@ant-design/icons'
import { Alert, App, Button, Col, Flex, Form, Input, List, Modal, Row, Spin, Typography } from 'antd'

import dayjs from 'dayjs'

import placeholder from '../../components/icon/assets/rider.png'
import { Title } from '../../components/text/Title'
import { useCommentsQuery, useCreateCommentMutation } from '../../graphql'
import { Context as AuthContext } from '../../providers/Auth'

import { AuthorMeta } from './ArticleCard'

const Auth = lazy(() => import('../../components/user/Auth'))

const Comments: FC<{ articleId?: string }> = ({ articleId }) => {
  const { slug = '' } = useParams()
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const { user } = useContext(AuthContext)
  const { notification } = App.useApp()
  const [open, setOpen] = useState(false)
  const [createComment] = useCreateCommentMutation()
  const { data, loading, refetch } = useCommentsQuery({ variables: { slug } })
  const onFormFinish = useCallback(
    (values: { comment: string }) => {
      if (user?.id && articleId) {
        createComment({ variables: { data: { user: user?.id, article: articleId, text: values.comment } } })
          .then(() => notification.success({ message: <FormattedMessage id={'article.comment.success'} /> }))
          .then(() => refetch({ slug }))
          .then(() => form.resetFields())
          .catch(() => notification.error({ message: <FormattedMessage id={'article.comment.error'} /> }))
      }
    },
    [user?.id, articleId, createComment, notification, refetch, slug, form],
  )

  return (
    <List
      header={
        <Title level={4}>
          <FormattedMessage id={'article.comments.header'} />
        </Title>
      }
      footer={
        <Flex vertical gap={8}>
          {!user?.username ? (
            <Alert
              type={'warning'}
              description={<FormattedMessage id={'article.comments.register-description'} />}
              action={
                <Link to={'#'} onClick={() => setOpen(true)}>
                  <FormattedMessage id={'article.comments.register'} />
                </Link>
              }
            />
          ) : null}
          <Form form={form} style={{ marginBottom: 32 }} layout={'vertical'} onFinish={onFormFinish}>
            <Row gutter={[16, 16]} justify={'center'} align={'middle'}>
              <Col style={{ height: 40 }} flex={'auto'}>
                <Form.Item
                  name={'comment'}
                  rules={[{ required: true, message: <FormattedMessage id={'form.required-field.error'} /> }]}
                >
                  <Input.TextArea style={{ resize: 'none' }} rows={2} disabled={!user?.username} />
                </Form.Item>
              </Col>
              <Col flex={'none'} style={{ marginTop: 20 }}>
                <Button
                  disabled={!user?.username}
                  shape={'circle'}
                  htmlType={'submit'}
                  icon={<CommentOutlined />}
                  title={formatMessage({ id: 'article.comment.post' })}
                />
              </Col>
            </Row>
          </Form>
          <Modal
            title={<FormattedMessage id={'auth.form.title'} />}
            open={open}
            onCancel={() => setOpen(false)}
            okButtonProps={{ hidden: true }}
            cancelButtonProps={{ hidden: true }}
          >
            <Suspense fallback={<Spin size={'small'} />}>
              <Auth onSuccess={() => setOpen(false)} />
            </Suspense>
          </Modal>
        </Flex>
      }
      split
      loading={loading}
      dataSource={data?.comments}
      rowKey={(item) => `${item?.documentId}`}
      renderItem={(it) => (
        <List.Item>
          <AuthorMeta
            avatar={placeholder}
            initials={it?.user?.username.slice(0, 1)}
            title={
              <Flex gap={8} align={'center'}>
                <Typography.Text type={it?.user?.username === user?.username ? 'warning' : undefined} strong>
                  {it?.user?.username}
                </Typography.Text>
                <Typography.Text type={'secondary'} style={{ fontSize: 12 }}>
                  {dayjs(it?.publishedAt).format('DD MMMM, HH:mm')}
                </Typography.Text>
              </Flex>
            }
            description={it?.text}
          />
        </List.Item>
      )}
    />
  )
}

export { Comments }
